import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/lib/utils';
import { Toaster } from '@/components/ui/Toaster';
import {
  LayoutDashboard,
  Package,
  Layers,
  MessageSquare,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Star,
  Award,
} from 'lucide-react';

export const AdminLayout = () => {
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, admin } = useAuthStore();

  const menuItems = [
    { icon: LayoutDashboard, label: 'Panel', path: '/admin/dashboard' },
    { icon: Layers, label: 'Kategoriler', path: '/admin/categories' },
    { icon: Star, label: 'Öne Çıkan Kategoriler', path: '/admin/featured-categories' },
    { icon: Package, label: 'Ürünler', path: '/admin/products' },
    { icon: Award, label: 'Öne Çıkan Ürünler', path: '/admin/featured-products' },
    { icon: MessageSquare, label: 'Talepler', path: '/admin/inquiries' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="flex h-screen bg-gray-100 font-body">
      {/* Mobile Sidebar Overlay */}
      <div
        className={cn(
          "fixed inset-0 z-20 bg-black/50 lg:hidden transition-opacity",
          isMobileSidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={() => setMobileSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed lg:static inset-y-0 left-0 z-30 bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out",
          // Mobile: slide in/out
          isMobileSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
          // Desktop: collapsed or expanded
          isSidebarCollapsed ? "lg:w-20" : "lg:w-64",
          // Mobile always full width when open
          "w-64"
        )}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-100">
          <span className={cn(
            "font-display font-bold text-xl tracking-tight transition-all duration-300",
            isSidebarCollapsed ? "lg:hidden" : ""
          )}>
            TURCO
          </span>
          <span className={cn(
            "font-display font-bold text-xl tracking-tight",
            isSidebarCollapsed ? "lg:block hidden" : "hidden"
          )}>
            T
          </span>

          {/* Collapse toggle - desktop only */}
          <button
            onClick={() => setSidebarCollapsed(!isSidebarCollapsed)}
            className="hidden lg:flex p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            {isSidebarCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <ChevronLeft className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 px-3 space-y-1">
          {menuItems.map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileSidebarOpen(false)}
                className={cn(
                  "flex items-center px-3 py-3 rounded-lg transition-colors relative group",
                  isActive
                    ? "bg-black text-white"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                )}
              >
                <item.icon
                  className={cn(
                    "w-5 h-5 flex-shrink-0",
                    isActive ? "text-white" : "text-gray-400 group-hover:text-gray-900"
                  )}
                />
                <span
                  className={cn(
                    "ml-3 font-medium text-sm whitespace-nowrap transition-all duration-300",
                    isSidebarCollapsed ? "lg:opacity-0 lg:w-0 lg:overflow-hidden" : ""
                  )}
                >
                  {item.label}
                </span>

                {/* Tooltip for collapsed state */}
                {isSidebarCollapsed && (
                  <div className="absolute left-full ml-3 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap hidden lg:block z-50">
                    {item.label}
                  </div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User Profile & Logout */}
        <div className="p-4 border-t border-gray-100">
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0">
              {admin?.name?.charAt(0) || 'A'}
            </div>
            <div
              className={cn(
                "ml-3 overflow-hidden transition-all duration-300 min-w-0",
                isSidebarCollapsed ? "lg:w-0 lg:opacity-0" : "flex-1"
              )}
            >
              <p className="text-sm font-medium text-gray-900 truncate">{admin?.name}</p>
              <p className="text-xs text-gray-400 truncate">{admin?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className={cn(
                "p-2 text-gray-400 hover:text-red-600 transition-colors flex-shrink-0",
                isSidebarCollapsed ? "lg:ml-0" : "ml-auto"
              )}
              title="Çıkış Yap"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header - Mobile only */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:hidden">
          <button
            onClick={() => setMobileSidebarOpen(!isMobileSidebarOpen)}
            className="p-2 -ml-2 text-gray-600 hover:text-gray-900"
          >
            {isMobileSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <span className="font-display font-medium text-lg">Yönetim Paneli</span>
          <div className="w-10" />
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      {/* Toast Notifications */}
      <Toaster />
    </div>
  );
};
